import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Settings,
  Globe2,
  Eye,
  TrendingUp,
  FolderOpen,
  MoreHorizontal,
  BarChart2,
  Inbox,
  Rocket,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { Tooltip } from '../../components/Tooltip';
import type { Site, Page } from '../../schemas/validation';

interface SiteManagerHeaderProps {
  site: Site;
  pages: Page[];
  siteId: string;
  isSavingTemplate: boolean;
  formatDate: (date: string) => string;
  onSaveTemplateClick: () => void;
  onSettingsClick: () => void;
  onSEOOverviewClick: () => void;
  onPreviewClick: () => void;
  onMediaLibraryClick: () => void;
  onAnalyticsClick: () => void;
  onSubmissionsClick?: () => void;
  /** Global unpublished changes count (from summary hook, owned by parent) */
  totalChanged: number;
  hasChanges: boolean;
  isSummaryLoading: boolean;
  isPublishingAll: boolean;
  onPublishAll: () => void;
}

export const SiteManagerHeader: React.FC<SiteManagerHeaderProps> = ({
  site,
  pages,
  siteId: _siteId,
  isSavingTemplate,
  formatDate,
  onSaveTemplateClick,
  onSettingsClick,
  onSEOOverviewClick,
  onPreviewClick,
  onMediaLibraryClick,
  onAnalyticsClick,
  onSubmissionsClick,
  totalChanged,
  hasChanges,
  isSummaryLoading,
  isPublishingAll,
  onPublishAll,
}) => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 gap-4">
          {/* Left side: Back link and site info */}
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <Link
              to="/dashboard"
              className="flex items-center space-x-1 sm:space-x-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{site.name}</h1>
              <p className="text-sm text-gray-600 hidden sm:block">
                Created {formatDate(site.createdAt)}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">
            {/* Publish All Changes button — visible when there are unpublished changes */}
            {hasChanges && !isSummaryLoading && (
              <button
                onClick={onPublishAll}
                disabled={isPublishingAll}
                className="relative inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-60"
              >
                {isPublishingAll ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="hidden sm:inline">Publishing...</span>
                  </>
                ) : (
                  <>
                    <Rocket size={16} />
                    <span className="hidden sm:inline">Publish Changes</span>
                    <span className="sm:hidden">Publish</span>
                    {/* Badge with count */}
                    <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-white text-green-700 rounded-full">
                      {totalChanged}
                    </span>
                  </>
                )}
              </button>
            )}

            {/* Primary actions - always visible */}
            <Tooltip
              content={pages.length === 0 ? 'Create a page first to preview' : 'Preview Website'}
            >
              <button
                onClick={onPreviewClick}
                disabled={pages.length === 0}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  pages.length === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600 hover:text-white hover:bg-blue-600'
                )}
              >
                <Eye size={20} />
              </button>
            </Tooltip>

            <Tooltip content="Website Settings">
              <button
                onClick={onSettingsClick}
                className="p-2 text-gray-600 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
              >
                <Settings size={20} />
              </button>
            </Tooltip>

            {/* Secondary actions - hidden on mobile, in dropdown */}
            <Tooltip content="Save as Template">
              <button
                onClick={onSaveTemplateClick}
                className="hidden sm:block p-2 text-green-600 hover:text-white hover:bg-green-600 rounded-lg transition-colors"
                disabled={isSavingTemplate}
              >
                <Globe2 size={20} />
              </button>
            </Tooltip>

            <Tooltip content="SEO Analysis">
              <button
                onClick={onSEOOverviewClick}
                className="hidden sm:block p-2 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors"
              >
                <TrendingUp size={20} />
              </button>
            </Tooltip>

            <Tooltip content="Analytics">
              <button
                onClick={onAnalyticsClick}
                className="hidden sm:block p-2 text-cyan-600 hover:text-white hover:bg-cyan-600 rounded-lg transition-colors"
              >
                <BarChart2 size={20} />
              </button>
            </Tooltip>

            {onSubmissionsClick && (
              <Tooltip content="Form Submissions">
                <button
                  onClick={onSubmissionsClick}
                  className="hidden sm:block p-2 text-orange-600 hover:text-white hover:bg-orange-600 rounded-lg transition-colors"
                >
                  <Inbox size={20} />
                </button>
              </Tooltip>
            )}

            <Tooltip content="Media Library">
              <button
                onClick={onMediaLibraryClick}
                className="hidden sm:block p-2 text-amber-600 hover:text-white hover:bg-amber-600 rounded-lg transition-colors"
              >
                <FolderOpen size={20} />
              </button>
            </Tooltip>

            {/* More menu - visible only on mobile */}
            <div className="relative sm:hidden" ref={moreMenuRef}>
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className="p-2 text-gray-600 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                aria-expanded={isMoreMenuOpen}
                aria-haspopup="true"
              >
                <MoreHorizontal size={20} />
              </button>

              {isMoreMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => {
                      setIsMoreMenuOpen(false);
                      onSaveTemplateClick();
                    }}
                    disabled={isSavingTemplate}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Globe2 size={16} className="mr-3 text-green-600" />
                    Save as Template
                  </button>
                  <button
                    onClick={() => {
                      setIsMoreMenuOpen(false);
                      onSEOOverviewClick();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <TrendingUp size={16} className="mr-3 text-indigo-600" />
                    SEO Analysis
                  </button>
                  <button
                    onClick={() => {
                      setIsMoreMenuOpen(false);
                      onAnalyticsClick();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <BarChart2 size={16} className="mr-3 text-cyan-600" />
                    Analytics
                  </button>
                  {onSubmissionsClick && (
                    <button
                      onClick={() => {
                        setIsMoreMenuOpen(false);
                        onSubmissionsClick();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Inbox size={16} className="mr-3 text-orange-600" />
                      Form Submissions
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsMoreMenuOpen(false);
                      onMediaLibraryClick();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FolderOpen size={16} className="mr-3 text-amber-600" />
                    Media Library
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
